import {
  leadOptionSelectedOccurred$,
  leadFormSubmittedOccurred$,
  leadFormClosedOccurred$,
  leadFormAbandonedOccurred$,
  leadFormSubmissionFailedOccurred$,
} from '../redux/trackingObservables';
import {
  startLeadFormSession,
  recordFieldInteraction,
  recordValidationErrors,
  markSubmitted,
  markSubmitAttempted,
  getElapsedSeconds,
  getFieldsCompletedCount,
  getLeadFormSessionSnapshot,
  resetLeadFormSession,
} from './leadFormTrackingState';
import { REALTOR_QUESTION_LABELS } from '../components/realtorDropdown';

export { recordFieldInteraction, recordValidationErrors };

/**
 * Fires when the user picks "Book appointment" or "Contact sales center" in the entry modal —
 * this is also where the session clock starts (drives time_to_submit/time_spent later).
 * `fromPage` is passed through as-is (raw data-from-page value) — this feature has no opinion on
 * what it means; the consuming page translates it into its own position/section vocabulary.
 * @param {{ buttonName: string, fromPage: string|undefined }} params
 */
export function trackLeadOptionSelected({ buttonName, fromPage }) {
  startLeadFormSession({ buttonName, fromPage });
  leadOptionSelectedOccurred$.next({ buttonName, fromPage });
}

/**
 * Fires on a successful submit, right before the lead is actually posted.
 * @param {{ date?: string, preferredContactMethod?: string, realtorQuestion?: string }} params
 */
export function trackLeadFormSubmitted({ date, preferredContactMethod, realtorQuestion }) {
  const { buttonName, fromPage } = getLeadFormSessionSnapshot();

  if (!buttonName) {
    // No active session — defensive only, shouldn't happen since callers only invoke this after
    // trackLeadOptionSelected started one (see requestATourForm.js/getMoreInformationForm.js).
    return;
  }

  const timeToSubmit = getElapsedSeconds();

  markSubmitted();

  // Per NHS20-9021 UAT feedback, GA4/Segment want realtor_question reported as the answer's label
  // (e.g. "I am buying without an agent"), not the raw '1'-'4' dropdown value.
  leadFormSubmittedOccurred$.next({
    buttonName,
    fromPage,
    date,
    preferredContactMethod,
    realtorQuestion: REALTOR_QUESTION_LABELS[realtorQuestion] ?? realtorQuestion,
    timeToSubmit,
  });
}

/**
 * Fires when a submit attempt fails. Only 'validation' is produced today — server-side failure
 * detection would require the shared brochure epics/API (also used by the unrelated Free
 * Brochure and SRP extended-card flows) to observe fetch success/failure, which is out of scope.
 * Also marks the session as submit-attempted — a Submit click can fail validation with zero
 * fields ever focused (e.g. prefilled fields left untouched), which `formStarted`
 * (focus-driven, see recordFieldInteraction) alone wouldn't catch — so trackLeadFormClosed still
 * treats it as an abandonable session if the user then closes without fixing it.
 * @param {string} errorType
 */
export function trackLeadFormSubmissionFailed(errorType) {
  const { buttonName, fromPage } = getLeadFormSessionSnapshot();

  if (!buttonName) {
    // No active session — defensive only, shouldn't happen since callers only invoke this after
    // trackLeadOptionSelected started one (see requestATourForm.js/getMoreInformationForm.js).
    return;
  }

  markSubmitAttempted();

  leadFormSubmissionFailedOccurred$.next({ buttonName, fromPage, errorType });
}

function isFieldEmpty(formElement, fieldName) {
  return !formElement?.elements?.namedItem(fieldName)?.value;
}

/**
 * Fires on every modal close (X button, outside click, or Escape) while a lead form is mounted.
 * Also fires the abandonment Subject when the close represents a real abandonment — the user had
 * started filling the form out, or attempted a submit that failed validation, but never
 * successfully submitted it.
 * @param {{ closeTarget: 'x_button'|'outside_click'|'escape', formElement: HTMLFormElement|null }} params
 */
export function trackLeadFormClosed({ closeTarget, formElement }) {
  const {
    buttonName, fromPage, formStarted, submitAttempted, submitted, lastFieldInteracted, errorFieldNames,
  } = getLeadFormSessionSnapshot();

  if (!buttonName) {
    // No active session — defensive only, shouldn't happen since callers only invoke this while
    // their own form is mounted (see requestATourForm.js/getMoreInformationForm.js bindEvents).
    return;
  }

  const fieldsCompletedCount = getFieldsCompletedCount(formElement);

  leadFormClosedOccurred$.next({ buttonName, fromPage, closeTarget, formStarted, fieldsCompletedCount });

  if ((formStarted || submitAttempted) && !submitted) {
    // Based on every field a failed submit flagged as invalid (errorFieldNames), not just
    // lastFieldInteracted — a Submit click can fail on fields the user never individually
    // focused (none populated, or only a few), so pinning this to "the last field touched" missed
    // required_missing whenever that field wasn't the (or wasn't among the) invalid one(s).
    const hasValidationError = errorFieldNames.size > 0;
    let errorType;
    if (hasValidationError) {
      const hasEmptyInvalidField = [...errorFieldNames].some((fieldName) => isFieldEmpty(formElement, fieldName));
      errorType = hasEmptyInvalidField ? 'required_missing' : 'invalid_format';
    }

    leadFormAbandonedOccurred$.next({
      buttonName,
      fromPage,
      timeSpent: getElapsedSeconds(),
      lastFieldInteracted,
      fieldsCompletedCount,
      hasValidationError,
      errorType,
    });
  }

  resetLeadFormSession();
}
